import { useEffect, useMemo, useState } from "react";

// Positive cache: bundleId -> icon URL
const iconCache = new Map<string, string>();
// Negative cache: bundleIds we've already looked up and got nothing back.
// Without this, every re-render re-fetches the same misses (the home-screen
// layout editor was firing 2k+ iTunes lookups during a single session).
const missCache = new Set<string>();
// In-flight requests so concurrent callers share a single fetch.
const inflight = new Map<string, Promise<void>>();

async function lookupOne(bundleId: string): Promise<void> {
  if (iconCache.has(bundleId) || missCache.has(bundleId)) return;
  const existing = inflight.get(bundleId);
  if (existing) return existing;

  const p = (async () => {
    try {
      const res = await fetch(
        `https://itunes.apple.com/lookup?bundleId=${encodeURIComponent(bundleId)}&country=US&limit=1`
      );
      if (!res.ok) {
        missCache.add(bundleId);
        return;
      }
      const data = await res.json();
      const result = data?.results?.[0];
      const icon = result?.artworkUrl512 || result?.artworkUrl100 || result?.artworkUrl60;
      if (icon) {
        iconCache.set(bundleId, icon);
      } else {
        missCache.add(bundleId);
      }
    } catch {
      missCache.add(bundleId);
    } finally {
      inflight.delete(bundleId);
    }
  })();
  inflight.set(bundleId, p);
  return p;
}

/**
 * Given a list of apps (with bundleId), resolve missing icons via iTunes Lookup API.
 * Returns a map of bundleId → iconUrl. Negative results are cached so we never
 * re-hit iTunes for the same bundleId twice in a session.
 */
export function useAppIconLookup(apps: Array<{ bundleId?: string; [key: string]: unknown }>) {
  // Stable signature so the effect doesn't re-run on every render when only
  // array identity changes (very common with drag-and-drop editors).
  const key = useMemo(() => {
    const ids = Array.from(
      new Set(
        (apps ?? [])
          .map((a) => a.bundleId)
          .filter((id): id is string => !!id)
      )
    ).sort();
    return ids.join("|");
  }, [apps]);

  const [iconMap, setIconMap] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    (apps ?? []).forEach((a) => {
      if (a.bundleId && iconCache.has(a.bundleId)) {
        seed[a.bundleId] = iconCache.get(a.bundleId)!;
      }
    });
    return seed;
  });

  useEffect(() => {
    if (!key) return;
    const ids = key.split("|").filter(Boolean);
    let cancelled = false;

    const seed: Record<string, string> = {};
    ids.forEach((id) => {
      if (iconCache.has(id)) seed[id] = iconCache.get(id)!;
    });
    setIconMap(seed);

    const todo = ids.filter((id) => !iconCache.has(id) && !missCache.has(id));
    if (todo.length === 0) return;

    (async () => {
      // Batch of 5 concurrent
      for (let i = 0; i < todo.length; i += 5) {
        if (cancelled) return;
        await Promise.all(todo.slice(i, i + 5).map(lookupOne));
        if (cancelled) return;
        const next: Record<string, string> = {};
        ids.forEach((id) => {
          if (iconCache.has(id)) next[id] = iconCache.get(id)!;
        });
        setIconMap(next);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [key]);

  return iconMap;
}
